import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import { useTypedParams } from '@sorare/routing';

import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';

import { Matches as DumbMatches } from '@sorare/gameplay/src/components/seo/Matches';
import { useLayoutData } from '@sorare/gameplay/src/pages/players/$slug/layout';

import { playerFootballGameToGeneric } from 'pages/seo/player/utils';

import type {
  PlayerFixturesQuery,
  PlayerFixturesQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const PLAYER_FIXTURES_QUERY = gql`
  query PlayerFixturesQuery(
    $slug: String!
    $cursor: String
    $futureCursor: String
  ) {
    football {
      player(slug: $slug) {
        slug
        futureGames(first: 20, after: $futureCursor) {
          nodes {
            id
            ...playerFootballGameToGeneric_game
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
        pastGames(first: 20, after: $cursor) {
          nodes {
            id
            ...playerFootballGameToGeneric_game
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${playerFootballGameToGeneric.fragments.game}
` as TypedDocumentNode<PlayerFixturesQuery, PlayerFixturesQueryVariables>;

const Fixtures = () => {
  const openDialog = useOpenDialog();
  const { slug } = useTypedParams<PageParams>();
  const { player } = useLayoutData();
  const displayName = player?.displayName;
  const [tab, setTab] = useState<'past' | 'future'>('future');
  const { data, loading, loadMore } = usePaginatedQuery(PLAYER_FIXTURES_QUERY, {
    variables: {
      slug: slug || '',
    },
    connection: 'GameConnection',
    skip: !slug,
  });
  const { futureGames: futureGamesRoot, pastGames } =
    data?.football.player || {};
  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        futureCursor: futureGamesRoot?.pageInfo.endCursor,
        cursor: pastGames?.pageInfo.endCursor,
      });
    },
    (tab === 'future' && !!futureGamesRoot?.pageInfo.hasNextPage) ||
      (tab === 'past' && !!pastGames?.pageInfo.hasNextPage),
    loading
  );

  const futureGames =
    futureGamesRoot?.nodes?.map(game =>
      playerFootballGameToGeneric({ game })
    ) || [];

  if (tab === 'future' && futureGames.length <= 1 && !loading) {
    setTab('past');
  }

  return (
    <>
      <NoIndex />
      <DumbMatches
        displayName={displayName}
        loading={loading}
        futureGames={futureGames}
        pastGames={(pastGames?.nodes || []).map(game =>
          playerFootballGameToGeneric({
            game,
            onClick: id => openDialog(DialogKey.game, id),
          })
        )}
        infiniteScrollLoader={<InfiniteScrollLoader />}
        tab={tab}
        setTab={setTab}
      />
    </>
  );
};

export default Fixtures;
